export const DEFAULT_TECHNIQUE_IMAGE =
  "https://daisyui.com/images/stock/photo-1635805737707-575885ab0820.jpg";

export const DEFAULT_TECHNIQUES_IMAGE = "/img/diapo/slider_judo_06.webp";

export const DEFAULT_TECHNIQUES_FILTERS = {
  mode: "all",
  search: "",
  ceinture: "all",
  famille: "all",
  uv2: "all",
  favoritesOnly: false,
  includeSelfDefense: false,
  sort: "nameAsc",
};

export const TECHNIQUE_MODES = [
  {
    value: "all",
    label: "Toutes les techniques",
  },
  {
    value: "uv2",
    label: "Révisions UV2",
  },
  {
    value: "kata",
    label: "Révisions Kata",
  },
  {
    value: "quiz",
    label: "Quiz technique",
  },
];

export const BELT_OPTIONS = [
  "Jaune",
  "Orange",
  "Verte",
  "Bleue",
  "Marron",
  "Noire",
];

export const FAMILY_OPTIONS = [
  "Jambe",
  "Hanche",
  "Epaule",
  "Bras",
  "Sutemi Waza",
  "Immobilisation",
  "Shime Waza",
  "Kansetsu-Waza",
  "Self Défense",
  "Kata",
  "Vocabulaire",
  "Les Bases",
  "Ukemi",
  "Arbitrage",
];

export const UV2_FICHES = [
  "1A",
  "1B",
  "1C",
  "1D",
  "1E",
  "1F",
  "1G",
  "1H",
  "1I",
  "1J",
  "1K",
  "1L",
  "1M",
  "1N",
  "1O",
  "1P",
  "1Q",
];

export function normalizeUv2Value(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/^FICHE\s+/i, "")
    .replace(/^UV2\s+/i, "")
    .replace(/\s+/g, "");
}

export function getTechniqueUv2List(technique) {
  const rawValue = String(technique?.uv2 || "");

  if (!rawValue.trim()) return [];

  return rawValue
    .split(/[,;/|]+/)
    .map((item) => normalizeUv2Value(item))
    .filter(Boolean)
    .filter((item) => UV2_FICHES.includes(item));
}

export function hasUv2Fiche(technique, fiche) {
  if (!fiche || fiche === "all") return true;

  const normalizedFiche = normalizeUv2Value(fiche);
  const techniqueFiches = getTechniqueUv2List(technique);

  return techniqueFiches.includes(normalizedFiche);
}

export function getUv2Fiches() {
  return UV2_FICHES;
}

export function getUv2FicheLabel(fiche) {
  if (!fiche || fiche === "all") return "Toutes les fiches";

  return `Fiche ${normalizeUv2Value(fiche)}`;
}

export function countTechniquesByUv2Fiche(techniques = []) {
  return UV2_FICHES.reduce((acc, fiche) => {
    acc[fiche] = techniques.filter((technique) =>
      hasUv2Fiche(technique, fiche),
    ).length;

    return acc;
  }, {});
}

export function getUserIdFromLocalStorage() {
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    return userInfo?.userId || userInfo?.id || null;
  } catch (error) {
    console.error("Erreur lors de la récupération de userId :", error);
    return null;
  }
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getTechniqueKey(technique, index = "") {
  if (technique?.id) return `technique-${technique.id}`;
  return `technique-fallback-${index}`;
}

export function dedupeTechniques(techniques = []) {
  const seen = new Set();

  return techniques.filter((technique, index) => {
    const key = getTechniqueKey(technique, index);

    if (seen.has(key)) {
      console.warn(
        "[TechniquesRevisionPage] Technique dupliquée ignorée :",
        key,
      );
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function isSelfDefenseTechnique(technique) {
  return normalizeText(technique?.famille).includes("self defense");
}

export function isKataTechnique(technique) {
  const famille = normalizeText(technique?.famille);
  const type = normalizeText(technique?.type);
  const nom = normalizeText(technique?.nom);

  return (
    famille.includes("kata") || type.includes("kata") || nom.includes("kata")
  );
}

export function sortTechniques(techniques = [], sort = "nameAsc") {
  const sorted = [...techniques];

  if (sort === "beltAsc") {
    const order = ["Jaune", "Orange", "Verte", "Bleue", "Marron", "Noire"];

    return sorted.sort((a, b) => {
      const indexA = order.indexOf(a?.ceinture);
      const indexB = order.indexOf(b?.ceinture);

      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
    });
  }

  if (sort === "familyAsc") {
    return sorted.sort((a, b) =>
      String(a?.famille || "").localeCompare(String(b?.famille || ""), "fr"),
    );
  }

  return sorted.sort((a, b) =>
    String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr"),
  );
}

export function filterTechniques(
  techniques = [],
  filters = DEFAULT_TECHNIQUES_FILTERS,
  favoriteIds = [],
) {
  const search = normalizeText(filters.search);

  const filtered = techniques.filter((technique) => {
    const searchableContent = normalizeText(
      [
        technique?.nom,
        technique?.traduction,
        technique?.famille,
        technique?.ceinture,
        technique?.uv2,
      ].join(" "),
    );

    if (filters.mode === "uv2" && filters.uv2 === "all") {
      return false;
    }

    const matchesMode =
      filters.mode === "all" ||
      filters.mode === "quiz" ||
      (filters.mode === "uv2" &&
        Boolean(technique?.uv2) &&
        hasUv2Fiche(technique, filters.uv2)) ||
      (filters.mode === "kata" && isKataTechnique(technique));

    const matchesSearch = !search || searchableContent.includes(search);

    const matchesCeinture =
      filters.ceinture === "all" || technique?.ceinture === filters.ceinture;

    const matchesFamille =
      filters.famille === "all" || technique?.famille === filters.famille;

    const matchesUv2 =
      filters.mode === "uv2" ? true : hasUv2Fiche(technique, filters.uv2);

    const matchesFavorites =
      !filters.favoritesOnly || favoriteIds.includes(technique.id);

    const matchesSelfDefense =
      filters.includeSelfDefense || !isSelfDefenseTechnique(technique);

    return (
      matchesMode &&
      matchesSearch &&
      matchesCeinture &&
      matchesFamille &&
      matchesUv2 &&
      matchesFavorites &&
      matchesSelfDefense
    );
  });

  return sortTechniques(filtered, filters.sort);
}

export function getCeintureBadgeClass(ceinture) {
  switch (normalizeText(ceinture)) {
    case "jaune":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-400/15 dark:text-yellow-200";
    case "orange":
      return "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200";
    case "verte":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
    case "bleue":
      return "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200";
    case "marron":
      return "bg-amber-900/15 text-amber-900 dark:bg-amber-700/20 dark:text-amber-200";
    case "noire":
      return "bg-slate-950 text-white dark:bg-white dark:text-slate-950";
    default:
      return "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/60";
  }
}

export function getTechniquesStats(techniques = [], favoriteIds = []) {
  const uv2 = techniques.filter((technique) => Boolean(technique?.uv2)).length;
  const kata = techniques.filter(isKataTechnique).length;
  const selfDefense = techniques.filter(isSelfDefenseTechnique).length;
  const families = new Set(
    techniques.map((technique) => technique?.famille).filter(Boolean),
  );

  return {
    total: techniques.length,
    uv2,
    kata,
    selfDefense,
    favorites: favoriteIds.length,
    families: families.size,
  };
}

export function shuffleArray(items = []) {
  return [...items].sort(() => Math.random() - 0.5);
}

export function buildQuizQuestions(techniques = [], count = 10) {
  const validTechniques = techniques.filter(
    (technique) => technique?.nom && technique?.image1_url,
  );

  return shuffleArray(validTechniques)
    .slice(0, count)
    .map((technique) => {
      const sameFamilyTechniques = validTechniques.filter(
        (item) =>
          item.id !== technique.id &&
          item.famille === technique.famille &&
          item.nom,
      );

      const fallbackTechniques = validTechniques.filter(
        (item) => item.id !== technique.id && item.nom,
      );

      const wrongAnswers = shuffleArray(
        sameFamilyTechniques.length >= 2
          ? sameFamilyTechniques
          : fallbackTechniques,
      )
        .slice(0, 2)
        .map((item) => item.nom);

      const options = shuffleArray([...wrongAnswers, technique.nom]);

      return {
        id: technique.id,
        image: technique.image1_url,
        correctAnswer: technique.nom,
        options,
        famille: technique.famille,
        ceinture: technique.ceinture,
        traduction: technique.traduction,
      };
    });
}
